import { usePostHog } from "posthog-js/react";
import { useState } from "react";

import type { MessageStatusChangeRequestV2, MessageV2Dto } from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { ArchiveIcon } from "@/components/Icons/ArchiveIcon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { ClockIcon } from "@/components/Icons/ClockIcon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { EyeOffIcon } from "@/components/Icons/EyeOffIcon";
import { Flag01Icon } from "@/components/Icons/Flag01Icon";
import { MarkerPin01Icon } from "@/components/Icons/MarkerPin01Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { getClosePostReasonLabel } from "@/helpers/community-feed";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { CommunityContentRail } from "@/product-patterns/community/CommunityContentRail";
import { useTranslation } from "@/translations";

import { Announcement02Icon } from "../Icons/Announcement02Icon";
import { CodeOfConductDeletionModal } from "./CodeOfConductDeletionModal";
import { CommunityMessageActivitySection } from "./CommunityMessageActivitySection";
import { CommunityMessageAttachments } from "./CommunityMessageAttachments";
import { CommunityMessageClosePostConfirmModal } from "./CommunityMessageClosePostConfirmModal";
import { CommunityMessageClosePostSoldReasonModal } from "./CommunityMessageClosePostSoldReasonModal";
import { CommunityMessageClosePostSolvedReasonModal } from "./CommunityMessageClosePostSolvedReasonModal";
import { CommunityMessageContent } from "./CommunityMessageContent";
import { CommunityMessageEditForm } from "./CommunityMessageEditForm";
import { CommunityPost } from "./CommunityPost";
import { CommunityPostReachDetailsModal } from "./CommunityPostReachDetailsModal";
import {
  getCommunityMessageStatus,
  MIN_COMMUNITY_POST_REACH_DETAILS_DATE,
  useData,
  useTrackCommunityPostViewCount,
} from "./helpers";
import { InformedAdminsModal } from "./InformedAdminsModal";
import { ReportPostModal } from "./ReportPostModal";
import { CommunityPostStatus } from "./types";

interface CommunityMessageProps {
  message: MessageV2Dto;
  isExpanded?: boolean;
  isActionable?: boolean;
  onJoinGroup?: ({ groupId }: { groupId: string }) => Promise<void>;
}
export function CommunityMessage({
  message,
  isExpanded,
  isActionable = true,
  onJoinGroup,
}: CommunityMessageProps): React.ReactNode {
  const response = useData(message);

  return (
    <CommunityMessageWithData
      {...response}
      {...{
        isExpanded,
        isActionable,
        onJoinGroup,
      }}
    />
  );
}

function CommunityMessageWithData({
  post,
  isMarkingAsRead,
  isDeleting,
  markAsRead,
  deletePost,
  isExpanded,
  onJoinGroup,
  isActionable,
}: ReturnType<typeof useData> & Omit<CommunityMessageProps, "message">): React.ReactNode {
  const [hasBeenInViewport, setHasBeenInViewport] = useState(false);
  const [isReachDetailsModalOpened, reachDetailsModalHandler] = useBool(false);
  const [closePostConfirmationModalState, setClosePostConfirmationModalState] = useState<{
    reason: MessageStatusChangeRequestV2["reason"];
    canArchive: boolean;
    canDelete: boolean;
    isOpened: boolean;
  }>({
    reason: "undefined",
    canArchive: false,
    canDelete: false,
    isOpened: false,
  });
  const [isSoldReasonModalOpened, soldReasonModalHandler] = useBool(false);
  const [isSolvedReasonModalOpened, solvedReasonModalHandler] = useBool(false);
  const [isEditing, editHandler] = useBool(false);
  const [isCancelEditModalOpen, cancelEditModalHandler] = useBool(false);
  const [isDeleteModalOpen, deleteHandler] = useBool(false);
  const [isInformedAdminsModalOpened, informedAdminsModalHandler] = useBool(false);
  const [isReportModalOpened, reportModalHandler] = useBool(false);

  const postHog = usePostHog();
  const { t, i18n } = useTranslation();
  const sessionUser = useSessionUser();
  const { onStartTracking, onCancelTracking } = useTrackCommunityPostViewCount({
    itemId: post?.id || "",
    type: "message",
    isEnabled: !post?.viewTracked,
  });

  const handleCancelEdit = () => {
    editHandler.setFalse();
    cancelEditModalHandler.setFalse();
  };

  const handleViewReachDetails = () => {
    reachDetailsModalHandler.setTrue();

    postHog.capture("clicked_view_reach_details", {
      messageId: post?.id,
      isOwnMessage: post?.author.id === sessionUser.id,
    });
  };

  if (!post) {
    return null;
  }

  const handleEnterViewport = () => {
    setHasBeenInViewport(true);
    onStartTracking();
  };

  const handleLeaveViewport = () => {
    if (!isMarkingAsRead && post.isUnread) {
      void markAsRead();
    }

    onCancelTracking();
  };

  const isOwnPost = post.author.id === sessionUser.id;
  const canDelete = post.canDeleteOwn || post.canDeleteOther;
  const menuActions: ContextMenuItem[] =
    !post || post?.deletedAt || !isActionable
      ? []
      : [
          {
            labels: {
              default: t("component.community-post.actions.mark-as-read"),
            },
            value: "mark-read",
            icon: <CheckIcon />,
            "data-testid": "post-context-menu-mark-read",
            callbackFn: () => void markAsRead(),
            isHidden: !post.isUnread,
          },
          {
            labels: {
              default: t("component.community-post.actions.report"),
            },
            value: "report",
            icon: <Flag01Icon />,
            "data-testid": "post-context-menu-report",
            callbackFn: reportModalHandler.toggle,
            isHidden: !post.canReport,
          },
          {
            labels: {
              default: t("common.action.edit"),
            },
            value: "edit",
            icon: <Edit05Icon />,
            "data-testid": "context-menu-edit-btn",
            callbackFn: editHandler.setTrue,
            isHidden: !post.canEdit,
          },
          {
            labels: {
              default: t("component.community-post.actions.close"),
            },
            value: "archive",
            icon: <CheckIcon />,
            "data-testid": "post-context-menu-archive",
            type: "trigger",
            // Hide the entire "Close" menu if it is already archived
            isHidden: !!post.archivedAt,
            subItems: [
              {
                labels: {
                  default: t("component.community-post.actions.close.solved"),
                },
                value: "solved",
                "data-testid": "post-context-menu-solved-option-btn",
                callbackFn: solvedReasonModalHandler.setTrue,
                isHidden: post.messageLabel !== "askHelp" || !isOwnPost || !post.canArchive,
              },
              {
                labels: {
                  default: getClosePostReasonLabel({ reasonType: "solvedNotDetailed", t }),
                },
                value: "solved-not-detailed",
                "data-testid": "post-context-menu-solved-no-detail-option-btn",
                callbackFn: () => {
                  setClosePostConfirmationModalState({
                    isOpened: true,
                    canArchive: post.canArchive,
                    canDelete: false,
                    reason: "solvedNotDetailed",
                  });
                },
                isHidden: post.messageLabel !== "inform" || !isOwnPost || !post.canArchive,
              },
              {
                labels: {
                  default: t("component.community-post.actions.close.sold"),
                },
                "data-testid": "post-context-menu-sold-option-btn",
                value: "sold",
                callbackFn: soldReasonModalHandler.setTrue,
                isHidden: post.messageLabel !== "sellItem" || !isOwnPost || !post.canArchive,
              },
              {
                labels: {
                  default: getClosePostReasonLabel({ reasonType: "mistake", t }),
                },
                value: "mistake",
                "data-testid": "post-context-menu-mistake-option-btn",
                callbackFn: () => {
                  setClosePostConfirmationModalState({
                    isOpened: true,
                    canArchive: false,
                    canDelete: canDelete,
                    reason: "mistake",
                  });
                },
                isHidden: !isOwnPost || !canDelete,
              },
              {
                labels: {
                  default: getClosePostReasonLabel({ reasonType: "noResponse", t }),
                },
                value: "no-response",
                "data-testid": "post-context-menu-no-response-option-btn",
                callbackFn: () => {
                  setClosePostConfirmationModalState({
                    isOpened: true,
                    canArchive: post.canArchive,
                    canDelete: false,
                    reason: "noResponse",
                  });
                },
                isHidden: post.messageLabel !== "askHelp" || !isOwnPost || !post.canArchive,
              },
              {
                labels: {
                  default: getClosePostReasonLabel({ reasonType: "irrelevant", t }),
                },
                value: "irrelevant",
                "data-testid": "post-context-menu-irrelevant-option-btn",
                callbackFn: () => {
                  setClosePostConfirmationModalState({
                    isOpened: true,
                    canArchive: post.canArchive && post.messageLabel !== "sellItem",
                    canDelete: canDelete,
                    reason: "irrelevant",
                  });
                },
                isHidden: !(post.canArchive && post.messageLabel !== "sellItem") && !canDelete,
              },
            ],
          },
          {
            labels: {
              default: t("component.community-post.actions.delete"),
            },
            value: "delete",
            icon: <Trash02Icon />,
            "data-testid": "context-menu-delete-btn",
            callbackFn: deleteHandler.toggle,
            isHidden: !canDelete || (!sessionUser.isAdmin && post.canArchive),
          },
        ];
  const isAnnouncement = post.type === "announcement" || post.type === "announcementReadOnly";
  const status = getCommunityMessageStatus(post);
  const isReachDetailsAvailable =
    (!!post.postedAt || !!post.scheduledAt) && post.author.isAdmin
      ? new Date(post.postedAt || post.scheduledAt!) >= MIN_COMMUNITY_POST_REACH_DETAILS_DATE
      : false;

  return (
    <>
      <CommunityPost.Root
        user={post.author}
        actions={menuActions}
        group={post.group}
        isUnread={post.isUnread}
        isReported={Boolean(post.reportedAt)}
        isArchivedOrDeleted={status === CommunityPostStatus.ARCHIVED || status === CommunityPostStatus.DELETED}
        renderHeaders={() => (
          <>
            {status === CommunityPostStatus.SCHEDULED && (
              <CommunityPost.Header
                variant="warning"
                title={t("component.community-post.header.title.scheduled", {
                  dateTime: formatDate(i18n, "datetime", post.scheduledAt!),
                })}
                data-testid="post-scheduled-date"
                icon={<ClockIcon />}
                onClick={isReachDetailsAvailable ? handleViewReachDetails : undefined}
              />
            )}
            {/* For super admins */}
            {status === CommunityPostStatus.DELETED && (
              <CommunityPost.Header
                variant="danger"
                title={t("component.community-post.header.title.deleted")}
                icon={<Trash02Icon />}
                onClick={isReachDetailsAvailable ? handleViewReachDetails : undefined}
              />
            )}
            {/* For admins */}
            {status === CommunityPostStatus.DELIVERED && sessionUser.isAdmin && isAnnouncement && (
              <CommunityPost.Header
                variant="success"
                title={t("component.community-post.header.title.delivered")}
                icon={<CheckIcon />}
                onClick={isReachDetailsAvailable ? handleViewReachDetails : undefined}
              />
            )}
            {/* For all users */}
            {status === CommunityPostStatus.ARCHIVED && (
              <CommunityPost.Header
                title={t("component.community-post.header.title.archived")}
                icon={<ArchiveIcon />}
                data-testid="archived-label"
                onClick={isReachDetailsAvailable ? handleViewReachDetails : undefined}
              />
            )}
            {/* For residents */}
            {post.isHiddenFromAdmins && (
              <CommunityPost.Header
                variant="default"
                title={t("component.community-post.header.title.internal")}
                icon={<EyeOffIcon />}
              />
            )}
          </>
        )}
        renderBadges={() => (
          <>
            {post.issueStatus === "solved" && (
              <Badge variant="success" data-testid="post-solved-label" icon={<CheckIcon />}>
                {t("component.community-post.label.solved")}
              </Badge>
            )}
            {post.projectConnection && <Badge icon={<MarkerPin01Icon />}>{post.projectConnection.name}</Badge>}
            {post.reportedAt && !post.deletedAt && (
              <Badge data-testid="flagged-label" variant="danger" icon={<Flag01Icon />}>
                {t("component.community-post.label.flagged")}
              </Badge>
            )}
            {post.hasAdminBeenInformed && !post.deletedAt && (
              <Button variant="ghostSecondary" size="sm" onClick={() => informedAdminsModalHandler.toggle()}>
                <Badge data-testid="post-informed-label">{t("component.community-post.label.shared")}</Badge>
              </Button>
            )}
            {isAnnouncement && (
              <Badge data-testid="announcement-label" variant="special" icon={<Announcement02Icon />}>
                {t("component.community-post.label.announcement")}
              </Badge>
            )}
          </>
        )}
        dateSubtitle={
          <>
            {post.postedAt && !post.updatedAt && formatDate(i18n, "datetime", post.postedAt)}
            {post.postedAt &&
              post.updatedAt &&
              `${formatDate(i18n, "datetime", post.postedAt)} • ${t("component.community-post.edited")}`}
          </>
        }
        onEnterViewport={handleEnterViewport}
        onLeaveViewport={handleLeaveViewport}
      >
        <Stack gap="md">
          <section className="flex w-full flex-1 flex-col gap-space-sm">
            {isDefined(post.deletedAt) && !sessionUser.isSuperAdmin && (
              <p className="whitespace-pre-line text-text-secondary">{t("component.community-post.content.removed")}</p>
            )}
            {/* Post editing form */}
            {(!post.deletedAt || sessionUser.isSuperAdmin) && isEditing && (
              <CommunityContentRail>
                <CommunityMessageEditForm message={post} onEdit={editHandler.setFalse} onCancel={handleCancelEdit} />
              </CommunityContentRail>
            )}
            {/* Post content */}
            {(!post.deletedAt || sessionUser.isSuperAdmin) && !isEditing && (
              <CommunityMessageContent
                message={post}
                shouldLoad={hasBeenInViewport}
                onJoinGroup={onJoinGroup}
                isExpanded={isExpanded}
              />
            )}
          </section>
          {!isEditing && <CommunityMessageAttachments message={post} />}
          {/* Post reactions & comments */}
          {!post.scheduledAt && (
            <CommunityMessageActivitySection message={post} isExpanded={isExpanded} isActionable={isActionable} />
          )}
        </Stack>
      </CommunityPost.Root>

      <ConfirmModal
        tone="primary"
        title={t("component.community-post.cancel-edit.modal.title")}
        description={t("component.community-post.cancel-edit.modal.description")}
        isLoading={false}
        onReject={cancelEditModalHandler.setFalse}
        onOpenChange={cancelEditModalHandler.set}
        rejectBtnProps={{
          "data-testid": "cancel-edit-modal-cancel",
        }}
        onResolve={handleCancelEdit}
        resolveBtnProps={{
          text: t("common.action.delete"),
          "data-testid": "cancel-edit-modal-confirm",
        }}
        isOpened={isCancelEditModalOpen}
        shouldCloseOnEsc
        data-testid="cancel-edit-modal"
      />
      {post.canDeleteOwn ? (
        <ConfirmModal
          title={t("component.community-post.delete.modal.title")}
          description={t("component.community-post.delete.modal.description")}
          isLoading={isDeleting}
          tone="error"
          onReject={deleteHandler.setFalse}
          onOpenChange={deleteHandler.set}
          rejectBtnProps={{
            "data-testid": "delete-modal-cancel",
          }}
          onResolve={async () => {
            await deletePost({
              messageId: post.id,
              reason: "undefined",
            });
            deleteHandler.setFalse();
          }}
          resolveBtnProps={{
            "data-testid": "modal-confirm-delete",
            text: t("common.action.delete"),
          }}
          isOpened={isDeleteModalOpen}
          shouldCloseOnEsc
          data-testid="cancel-edit-modal"
        />
      ) : (
        <CodeOfConductDeletionModal
          isLoading={isDeleting}
          isOpened={isDeleteModalOpen}
          onOpenChange={deleteHandler.set}
          onDelete={async () => {
            await deletePost({
              messageId: post.id,
              reason: "codeOfConductViolation",
            });
            deleteHandler.setFalse();
          }}
        />
      )}
      <InformedAdminsModal
        isOpened={isInformedAdminsModalOpened}
        onOpenChange={informedAdminsModalHandler.set}
        messageId={post.id}
      />
      <ReportPostModal isOpened={isReportModalOpened} onOpenChange={reportModalHandler.set} messageId={post.id} />

      <CommunityMessageClosePostConfirmModal
        messageId={post.id}
        {...closePostConfirmationModalState}
        onOpenChange={(state) =>
          setClosePostConfirmationModalState((currState) => ({
            ...currState,
            isOpened: state,
          }))
        }
      />
      <CommunityMessageClosePostSoldReasonModal
        messageId={post.id}
        isOpened={isSoldReasonModalOpened}
        onOpenChange={soldReasonModalHandler.set}
      />
      <CommunityMessageClosePostSolvedReasonModal
        messageId={post.id}
        isOpened={isSolvedReasonModalOpened}
        onOpenChange={solvedReasonModalHandler.set}
      />
      {status === CommunityPostStatus.SCHEDULED && post.id && (
        <CommunityPostReachDetailsModal
          isOpened={isReachDetailsModalOpened}
          onOpenChange={reachDetailsModalHandler.set}
          postId={post.id}
          onEditMessage={editHandler.setTrue}
          status={status}
          postType="message"
        />
      )}
      {(status === CommunityPostStatus.DELIVERED ||
        status === CommunityPostStatus.DELETED ||
        status === CommunityPostStatus.ARCHIVED) &&
        post.id && (
          <CommunityPostReachDetailsModal
            isOpened={isReachDetailsModalOpened}
            onOpenChange={reachDetailsModalHandler.set}
            postId={post.id}
            status={status}
            postType="message"
          />
        )}
    </>
  );
}
